import React from "react";
import { Link } from "react-router-dom";
import { TRIAL } from "../../data/site";

/**
 * Everything a customer must see and actively agree to before a payment obligation starts.
 * Both checkboxes start unchecked — never pre-tick them.
 */
export const CheckoutConsent = ({ plan, annual, amount, consent, setConsent }) => {
  const period = annual ? "year" : "month";
  const rows = [
    ["Selected plan", plan.name],
    ["Recurring price", `€${amount} per ${period}, excluding VAT`],
    ["VAT", "Calculated at checkout based on your billing country and shown on your invoice"],
    ["Billing frequency", annual ? "Once per year, in advance" : "Once per month, in advance"],
    ["Trial", `${TRIAL.days} days free, then €${amount} per ${period} excl. VAT is charged to this card automatically. Cancel during the trial and you pay nothing.`],
    ["Automatic renewal", `Renews automatically every ${period} for the same amount until you cancel`],
    ["Cancelling", "Cancel any time on this page. Access continues to the end of the period you paid for."],
  ];

  const toggle = (key) => (e) => setConsent((c) => ({ ...c, [key]: e.target.checked }));

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-[#FAFAFA] p-4 sm:p-5" data-testid="checkout-summary">
      <p className="font-display text-base font-semibold text-slate-900">What you're agreeing to</p>
      <dl className="mt-3 space-y-1.5 text-xs leading-relaxed text-slate-600">
        {rows.map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <dt className="w-32 shrink-0 font-semibold text-slate-500">{label}</dt>
            <dd className="min-w-0 flex-1">{value}</dd>
          </div>
        ))}
      </dl>

      <label htmlFor="checkout-terms" className="mt-4 flex items-start gap-3 text-sm text-slate-700">
        <input
          id="checkout-terms"
          type="checkbox"
          data-testid="checkout-terms-checkbox"
          className="mt-1 h-4 w-4 accent-[#F95738]"
          checked={consent.terms}
          onChange={toggle("terms")}
        />
        <span>
          I agree to the{" "}
          <Link to="/legal/terms" target="_blank" className="font-semibold text-[#F95738] hover:underline">
            Terms of Service
          </Link>{" "}
          and acknowledge the{" "}
          <Link to="/legal/privacy-policy" target="_blank" className="font-semibold text-[#F95738] hover:underline">
            Privacy Policy
          </Link>
          .
        </span>
      </label>

      <label htmlFor="checkout-immediate" className="mt-3 flex items-start gap-3 text-sm text-slate-700">
        <input
          id="checkout-immediate"
          type="checkbox"
          data-testid="checkout-immediate-consent-checkbox"
          className="mt-1 h-4 w-4 accent-[#F95738]"
          checked={consent.immediate}
          onChange={toggle("immediate")}
        />
        <span>
          I ask PostMint to start the paid service immediately and I understand that, once it has been fully performed,
          I lose my statutory right of withdrawal. See the{" "}
          <Link to="/legal/refund-policy" target="_blank" className="font-semibold text-[#F95738] hover:underline">
            Refund &amp; Cancellation Policy
          </Link>
          .
        </span>
      </label>

      {(!consent.terms || !consent.immediate) && (
        <p className="mt-3 text-xs text-slate-500" data-testid="checkout-consent-hint">
          Tick both boxes to continue to payment.
        </p>
      )}
    </div>
  );
};

export default CheckoutConsent;
